import React, { useState, useCallback, useEffect } from 'react';
import { FilePair, matchFiles, ParsedFile, getPairingRule } from '../../utils/fileMatcher';
import { Upload, X, FileText, Link2, Unlink, AlertTriangle, CheckCircle } from 'lucide-react';
import TokenBadge from '../shared/TokenBadge';
import { parseFile, isSupportedFile, getFormatLabel, ACCEPTED_FILE_TYPES } from '../../utils/fileParser';
import { extractTextXmls, buildFilteredZip, XmlTooLargeError, peekMainXmlUncompressedSize, MAX_MAIN_XML_UNCOMPRESSED_BYTES } from '../../utils/clientDocxUnzip';
import { FileTooLargeError } from '../../hooks/useFileActions';
import { apiParseDocxStateless, apiParsePptxStateless, type ParseStatelessResponse } from '../../services/api/docxApi';
import { useAccurateTokenCount } from '../../hooks/useAccurateTokenCount';
import { selectPrimaryCredits, type CreditMode } from '../../utils/creditSelector';
import { getLanguage } from '../../utils/languages';

interface BatchUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (pairs: FilePair[]) => void;
    mode?: 'train' | 'translate';
    sourceLang?: string;
    targetLang?: string;
}

const BatchUploadModal: React.FC<BatchUploadModalProps> = ({
    isOpen, onClose, onImport, mode = 'train',
    sourceLang = 'vi', targetLang = 'en',
}) => {
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [pairs, setPairs] = useState<FilePair[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isEstimating, setIsEstimating] = useState(false);
    const [parseStatus, setParseStatus] = useState<Map<string, 'parsing' | 'done' | 'error'>>(new Map());
    const [parseWarnings, setParseWarnings] = useState<Map<string, string[]>>(new Map());
    const [parsedTexts, setParsedTexts] = useState<Map<string, string>>(new Map());
    const [parsedBenchmarkTexts, setParsedBenchmarkTexts] = useState<Map<string, string>>(new Map());
    const [accurateStatuses, setAccurateStatuses] = useState<Map<string, 'idle' | 'counting' | 'ok' | 'error'>>(new Map());
    const [bundleCredits, setBundleCredits] = useState<Map<string, FilePair['estimatedCredits']>>(new Map());
    const [parsedResults, setParsedResults] = useState<Map<string, ParseStatelessResponse>>(new Map());
    const { countBundle } = useAccurateTokenCount();
    const creditMode: CreditMode = mode === 'train' ? 'train' : 'translate';

    // Parse files + fire accurate count whenever pairs change
    useEffect(() => {
        if (pairs.length === 0) {
            return;
        }

        let cancelled = false;
        setIsEstimating(true);

        const runAccurateCount = async (pairId: string, text: string, benchmarkText?: string) => {
            const segments = text.split('\n').filter((s: string) => s.trim().length > 0);
            const humanSegments = benchmarkText
                ? benchmarkText.split('\n').filter((s: string) => s.trim().length > 0)
                : undefined;

            // Bundle endpoint — returns full breakdown (translate + qc OR translate + match + audit)
            const bundle = await countBundle({
                bundle_type: mode === 'train' ? 'train' : 'translate',
                segments,
                humanSegments,
                sourceLang,
                targetLang,
            });
            if (cancelled) return;

            if (!bundle) {
                setAccurateStatuses((prev: Map<string, 'idle' | 'counting' | 'ok' | 'error'>) => new Map(prev).set(pairId, 'error'));
                return;
            }
            // Save bundle credits — TokenBadge resolves primary credits via selectPrimaryCredits(creditMode)
            setBundleCredits((prev: Map<string, FilePair['estimatedCredits']>) => new Map(prev).set(pairId, {
                translate: bundle.breakdown.translate,
                qc: bundle.breakdown.qc,
                semanticMatch: bundle.breakdown.semanticMatch,
                audit: bundle.breakdown.audit,
                pricingVersion: bundle.pricingVersion,
                bundleType: bundle.bundleType,
                computedAt: bundle.computedAt,
            }));
            setAccurateStatuses((prev: Map<string, 'idle' | 'counting' | 'ok' | 'error'>) => new Map(prev).set(pairId, 'ok'));
        };

        const parseAndCount = async () => {
            const newParseStatus = new Map<string, 'parsing' | 'done' | 'error'>();
            const newWarnings = new Map<string, string[]>();
            const newTexts = new Map<string, string>();
            const newBenchmarkTexts = new Map<string, string>();
            const newAccurateStatuses = new Map<string, 'idle' | 'counting' | 'ok' | 'error'>();

            const newParsedResults = new Map<string, ParseStatelessResponse>();

            // DOCX/PPTX: stateless flow (client unzip whitelist → server parse) — không freeze + không leak parser
            // PDF/TXT/MD: parseFile cũ (file nhỏ, không cần whitelist)
            const parseSourceFile = async (file: File): Promise<{ text: string; parsedResult: ParseStatelessResponse | null; warnings?: string[] }> => {
                const lower = file.name.toLowerCase();
                const isDocx = lower.endsWith('.docx');
                const isPptx = lower.endsWith('.pptx');
                if (isDocx || isPptx) {
                    const buf = await file.arrayBuffer();
                    // extractTextXmls throws XmlTooLargeError nếu document.xml > 15MB uncompressed
                    let xmls: Record<string, string>;
                    try {
                        xmls = await extractTextXmls(buf, isDocx ? 'docx' : 'pptx');
                    } catch (err) {
                        if (err instanceof XmlTooLargeError) {
                            throw new FileTooLargeError(file.name, 0);
                        }
                        throw err;
                    }
                    const filteredZip = await buildFilteredZip(xmls);
                    const result = isDocx
                        ? await apiParseDocxStateless(filteredZip)
                        : await apiParsePptxStateless(filteredZip);
                    // Surface server warnings (e.g. PDF_CONVERT_DETECTED — file convert PDF)
                    const serverWarnings = (result as any).warnings?.map((w: any) =>
                        w.code === 'PDF_CONVERT_DETECTED'
                            ? `⚠️ File convert từ PDF — bản dịch có khả năng CAO bị vỡ format hoặc dịch sai. Khuyến nghị: re-create từ Word gốc.`
                            : `⚠️ ${w.message}`
                    ) || [];
                    return { text: result.segments.join('\n'), parsedResult: result, warnings: serverWarnings.length > 0 ? serverWarnings : undefined };
                }
                const result = await parseFile(file);
                return { text: result.text, parsedResult: null, warnings: result.warnings };
            };

            // Combined size cap per pair: source + benchmark ≤ 15MB document.xml uncompressed
            // Peek ZIP central directory only (no decompression) — fast O(1) check
            const checkPairSizeCap = async (pair: FilePair): Promise<void> => {
                if (!pair.source) return;
                const peekFile = async (file: File): Promise<number> => {
                    const lower = file.name.toLowerCase();
                    if (lower.endsWith('.docx')) return peekMainXmlUncompressedSize(file, 'docx');
                    if (lower.endsWith('.pptx')) return peekMainXmlUncompressedSize(file, 'pptx');
                    return 0; // PDF/TXT/MD — no peek check, parseFile path
                };
                const sourceSize = await peekFile(pair.source.file);
                const benchSize = pair.benchmark ? await peekFile(pair.benchmark.file) : 0;
                const total = sourceSize + benchSize;
                if (total > MAX_MAIN_XML_UNCOMPRESSED_BYTES) {
                    throw new FileTooLargeError(pair.source.file.name, 0);
                }
            };

            for (const pair of pairs) {
                if (cancelled) return;
                if (pair.source) {
                    try {
                        newParseStatus.set(pair.id, 'parsing');
                        setParseStatus(new Map(newParseStatus));

                        // Check combined size cap (source + benchmark) trước khi extract
                        await checkPairSizeCap(pair);

                        const { text, parsedResult, warnings } = await parseSourceFile(pair.source.file);
                        newTexts.set(pair.id, text);
                        if (parsedResult) newParsedResults.set(pair.id, parsedResult);
                        newParseStatus.set(pair.id, 'done');
                        newAccurateStatuses.set(pair.id, 'counting');

                        if (warnings && warnings.length > 0) {
                            newWarnings.set(pair.id, warnings);
                        }

                        // Parse benchmark for train mode (audit factor needs human segments)
                        if (mode === 'train' && pair.benchmark) {
                            try {
                                const benchmark = await parseSourceFile(pair.benchmark.file);
                                newBenchmarkTexts.set(pair.id, benchmark.text);
                            } catch (err) {
                                console.warn(`Benchmark parse failed for ${pair.id}:`, err);
                            }
                        }
                    } catch (err) {
                        newParseStatus.set(pair.id, 'error');
                        if (err instanceof FileTooLargeError) {
                            // Friendly message — không show số token kỹ thuật, chỉ nói file quá lớn
                            newWarnings.set(pair.id, [`📄 File quá lớn — vui lòng chia thành 2-3 phần nhỏ rồi tải lại nhé`]);
                        } else {
                            newWarnings.set(pair.id, [`⚠️ Không đọc được file — file có thể bị lỗi hoặc không đúng định dạng`]);
                        }
                    }
                }
            }

            if (!cancelled) {
                setParseStatus(new Map(newParseStatus));
                setParseWarnings(new Map(newWarnings));
                setParsedTexts(newTexts);
                setParsedBenchmarkTexts(newBenchmarkTexts);
                setParsedResults(newParsedResults);
                setAccurateStatuses(new Map(newAccurateStatuses));
                setIsEstimating(false);

                // Fire accurate count cho tất cả file đã parse xong (US-04-10 — chỉ nguồn duy nhất)
                // US-11-09: bundle endpoint — train mode passes benchmark text for audit factor.
                newTexts.forEach((text, pairId) => {
                    runAccurateCount(pairId, text, newBenchmarkTexts.get(pairId));
                });
            }
        };

        parseAndCount();
        return () => { cancelled = true; };
    }, [pairs, countBundle, mode]);

    // Retry nếu user click "Thử lại" — re-fire bundle endpoint
    const retryAccurateCount = useCallback(async (pairId: string) => {
        const text = parsedTexts.get(pairId);
        if (!text) return;
        setAccurateStatuses((prev: Map<string, 'idle' | 'counting' | 'ok' | 'error'>) => new Map(prev).set(pairId, 'counting'));
        const segments = text.split('\n').filter((s: string) => s.trim().length > 0);
        // Train mode: include benchmark segments so audit factor matches initial count path
        const benchmarkText = mode === 'train' ? parsedBenchmarkTexts.get(pairId) : undefined;
        const humanSegments = benchmarkText
            ? benchmarkText.split('\n').filter((s: string) => s.trim().length > 0)
            : undefined;
        const bundle = await countBundle({
            bundle_type: mode === 'train' ? 'train' : 'translate',
            segments,
            humanSegments,
            sourceLang,
            targetLang,
        });
        if (!bundle) {
            setAccurateStatuses((prev: Map<string, 'idle' | 'counting' | 'ok' | 'error'>) => new Map(prev).set(pairId, 'error'));
            return;
        }
        setBundleCredits((prev: Map<string, FilePair['estimatedCredits']>) => new Map(prev).set(pairId, {
            translate: bundle.breakdown.translate,
            qc: bundle.breakdown.qc,
            semanticMatch: bundle.breakdown.semanticMatch,
            audit: bundle.breakdown.audit,
            pricingVersion: bundle.pricingVersion,
            bundleType: bundle.bundleType,
            computedAt: bundle.computedAt,
        }));
        setAccurateStatuses((prev: Map<string, 'idle' | 'counting' | 'ok' | 'error'>) => new Map(prev).set(pairId, 'ok'));
    }, [parsedTexts, parsedBenchmarkTexts, countBundle, mode]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files).filter(
            (f: File) => isSupportedFile(f.name)
        );

        if (files.length > 0) {
            const fileList = files as File[];
            setDroppedFiles(fileList);
            if (mode === 'translate') {
                // In translate mode, every file is a source-only file
                setPairs(fileList.map(f => ({
                    id: Math.random().toString(36).substr(2, 9),
                    source: { file: f, content: '' },
                    benchmark: null,
                    status: 'source_only'
                })));
            } else {
                setPairs(matchFiles(fileList, sourceLang, targetLang));
            }
        }
    }, [mode]);

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []).filter(
            (f: File) => isSupportedFile(f.name)
        );
        // Reset input value so selecting same file again triggers onChange
        e.target.value = '';

        if (files.length === 0) return;

        const fileList = files as File[];

        // Append to existing pairs (so "Nạp thêm" extends instead of replacing).
        // - translate mode: append new source_only entries, dedupe by file name.
        // - train mode: merge existing + new file objects, re-run matchFiles to recompute pairing.
        setPairs(prev => {
            if (prev.length === 0) {
                // Initial drop — original behavior
                if (mode === 'translate') {
                    return fileList.map(f => ({
                        id: Math.random().toString(36).substr(2, 9),
                        source: { file: f, content: '' },
                        benchmark: null,
                        status: 'source_only' as const,
                    }));
                }
                return matchFiles(fileList, sourceLang, targetLang);
            }

            // Append path
            if (mode === 'translate') {
                const existingNames = new Set(prev.map(p => p.source?.file.name).filter(Boolean));
                const additions = fileList
                    .filter(f => !existingNames.has(f.name))
                    .map(f => ({
                        id: Math.random().toString(36).substr(2, 9),
                        source: { file: f, content: '' },
                        benchmark: null,
                        status: 'source_only' as const,
                    }));
                return [...prev, ...additions];
            }

            // train mode: rebuild full file list then re-pair
            const allFiles: File[] = [];
            for (const p of prev) {
                if (p.source?.file) allFiles.push(p.source.file);
                if (p.benchmark?.file) allFiles.push(p.benchmark.file);
            }
            const existingFileNames = new Set(allFiles.map(f => f.name));
            for (const f of fileList) {
                if (!existingFileNames.has(f.name)) allFiles.push(f);
            }
            return matchFiles(allFiles, sourceLang, targetLang);
        });

        // setDroppedFiles still used by some downstream effects — accumulate for translate mode
        setDroppedFiles(prev => {
            const existingNames = new Set(prev.map(f => f.name));
            const additions = fileList.filter(f => !existingNames.has(f.name));
            return [...prev, ...additions];
        });
    }, [mode]);


    const handleRemovePair = (id: string) => {
        setPairs(prev => prev.filter(p => p.id !== id));
    };

    const handleUnlink = (id: string) => {
        setPairs(prev => prev.map(p => {
            if (p.id !== id) return p;
            // Split into two entries
            return { ...p, benchmark: null, status: 'source_only' as const };
        }));
    };

    const handleImport = () => {
        const validPairs = pairs
            .filter(p => p.source)
            .map(p => ({
                ...p,
                estimatedCredits: bundleCredits.get(p.id),  // US-11-09: bundle credits → file.estimatedCredits
                parsedResult: parsedResults.get(p.id),       // pass server-parsed result để parseOneFile reuse, skip API call thứ 2
                benchmarkText: parsedBenchmarkTexts.get(p.id), // pass benchmark text (train mode) → handleBatchImport skip parseFile lần 2
            }));
        onImport(validPairs);
        handleClear();
        onClose();
    };

    const handleClear = () => {
        setDroppedFiles([]);
        setPairs([]);
        setParseStatus(new Map());
        setParseWarnings(new Map());
        setParsedTexts(new Map());
        setBundleCredits(new Map());
        setAccurateStatuses(new Map());
    };

    // Translate mode: file source-only IS hợp lệ (không cần benchmark). Train mode: phải paired.
    // Exclude file đã parse fail (status='error') khỏi count + button enable — tránh user
    // bấm "Nạp" với file lỗi rồi báo bug "vào chưa được" (verified Threads cohort 2026-05-04).
    const okPair = (p: FilePair) => parseStatus.get(p.id) !== 'error';
    const pairedCount = mode === 'translate'
        ? pairs.filter(p => p.source !== null && okPair(p)).length
        : pairs.filter(p => p.status === 'paired' && okPair(p)).length;
    const sourceOnlyCount = pairs.filter(p => p.status === 'source_only').length;
    const orphanCount = pairs.filter(p => p.status === 'benchmark_only').length;
    const importableCount = pairs.filter(p => p.source && okPair(p)).length;


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Nạp tài liệu</h2>
                        <p className="text-sm text-slate-500 inline-flex items-center gap-1 flex-wrap">
                            {mode === 'translate' ? 'Kéo thả các tài liệu bạn muốn dịch vào đây' : (
                                <>
                                    Kéo thả ĐỒNG THỜI bản gốc
                                    {getLanguage(sourceLang)?.countryCode && (
                                        <img src={`https://flagcdn.com/16x12/${getLanguage(sourceLang)?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(sourceLang)?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                    )}
                                    ({sourceLang.toUpperCase()}) và bản chuẩn
                                    {getLanguage(targetLang)?.countryCode && (
                                        <img src={`https://flagcdn.com/16x12/${getLanguage(targetLang)?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(targetLang)?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                    )}
                                    ({targetLang.toUpperCase()})
                                </>
                            )}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {pairs.length === 0 ? (
                        /* Drop Zone */
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${isDragging
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
                                }`}
                            onClick={() => document.getElementById('batch-file-input')?.click()}
                        >
                            <input
                                id="batch-file-input"
                                type="file"
                                multiple
                                accept={ACCEPTED_FILE_TYPES}
                                className="hidden"
                                onChange={handleFileInput}
                            />
                            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-indigo-500' : 'text-slate-400'}`} />
                            <p className="text-lg font-bold text-slate-700 mb-2">
                                {mode === 'translate' ? 'Kéo thả tài liệu vào đây' : 'Kéo thả toàn bộ tài liệu vào đây'}
                            </p>
                            <p className="text-sm text-slate-500 mb-4 inline-flex items-center gap-1 flex-wrap justify-center">
                                {mode === 'translate' ? 'Hoặc click để chọn file từ máy tính của bạn.' : (
                                    <>
                                        Hệ thống tự động ghép cặp Bản gốc
                                        {getLanguage(sourceLang)?.countryCode && (
                                            <img src={`https://flagcdn.com/16x12/${getLanguage(sourceLang)?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(sourceLang)?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                        )}
                                        ({sourceLang.toUpperCase()}) và Bản chuẩn
                                        {getLanguage(targetLang)?.countryCode && (
                                            <img src={`https://flagcdn.com/16x12/${getLanguage(targetLang)?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(targetLang)?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                        )}
                                        ({targetLang.toUpperCase()}) dựa vào tên file.
                                    </>
                                )}
                            </p>
                            {mode !== 'translate' && (
                                <div className="mt-4 text-left inline-block bg-white p-4 rounded-xl border border-slate-200">
                                    <p className="text-sm font-bold text-slate-700 mb-2">Quy tắc đặt tên file:</p>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Đặt mã ngôn ngữ + dấu chấm <span className="font-bold text-slate-700">đầu tên file</span>. Phần còn lại của tên giữ giống hệt nhau ở 2 file:
                                    </p>
                                    <div className="bg-slate-50 px-3 py-2 rounded font-mono text-sm text-slate-700">
                                        {getPairingRule(sourceLang, targetLang)}
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1 flex-wrap">
                                        Ví dụ:
                                        {getLanguage(sourceLang)?.countryCode && (
                                            <img src={`https://flagcdn.com/16x12/${getLanguage(sourceLang)?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(sourceLang)?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                        )}
                                        <span className="font-mono">{sourceLang.toUpperCase()}.bao-cao-2024.docx</span>
                                        <span>&amp;</span>
                                        {getLanguage(targetLang)?.countryCode && (
                                            <img src={`https://flagcdn.com/16x12/${getLanguage(targetLang)?.countryCode}.png`} srcSet={`https://flagcdn.com/32x24/${getLanguage(targetLang)?.countryCode}.png 2x`} alt="" className="inline-block rounded-sm shrink-0" width={16} height={12} />
                                        )}
                                        <span className="font-mono">{targetLang.toUpperCase()}.bao-cao-2024.docx</span>
                                    </p>
                                    <p className="text-[10px] text-amber-600 mt-3 italic">*Mẹo: Chọn tất cả các file cùng lúc rồi kéo thả vào vùng này để làm nhanh nhất.</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Pairing Preview */
                        <div>
                            {/* Token Summary Card đã ẩn — credit hiển thị inline mỗi file row + totals ở footer modal */}
                            {mode === 'train' && isEstimating && (
                                <div style={{
                                    padding: '12px 16px',
                                    borderRadius: '12px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    marginBottom: '16px',
                                    fontSize: '13px',
                                    color: '#64748b',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                        Dang parse va uoc luong tokens...
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {pairs.filter(p => p.source).map(pair => {
                                            const status = parseStatus.get(pair.id);
                                            return (
                                                <span key={pair.id} style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    padding: '2px 8px', borderRadius: '6px', fontSize: '11px',
                                                    backgroundColor: status === 'done' ? '#ecfdf5' : status === 'error' ? '#fef2f2' : '#f1f5f9',
                                                    color: status === 'done' ? '#065f46' : status === 'error' ? '#991b1b' : '#475569',
                                                }}>
                                                    {status === 'parsing' && '...'}
                                                    {status === 'done' && String.fromCharCode(10003)}
                                                    {status === 'error' && String.fromCharCode(10007)}
                                                    {pair.source!.file.name}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-4 mb-6">
                                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                    <span className="text-sm font-semibold text-emerald-700">{pairedCount} file hợp lệ</span>
                                </div>
                                {/* Train-only: cảnh báo file thiếu bản chuẩn (translate mode không cần benchmark) */}
                                {mode === 'train' && sourceOnlyCount > 0 && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                        <span className="text-sm font-semibold text-amber-700">{sourceOnlyCount} thiếu bản chuẩn</span>
                                    </div>
                                )}
                                {orphanCount > 0 && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                                        <Unlink className="w-4 h-4 text-red-500" />
                                        <span className="text-sm font-semibold text-red-700">{orphanCount} chưa khớp</span>
                                    </div>
                                )}
                                <button
                                    onClick={() => document.getElementById('batch-file-input-add')?.click()}
                                    className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold transition-colors"
                                    title="Nạp thêm file vào danh sách hiện tại"
                                >
                                    <Upload className="w-4 h-4" />
                                    Nạp thêm
                                </button>
                                <input
                                    id="batch-file-input-add"
                                    type="file"
                                    multiple
                                    accept={ACCEPTED_FILE_TYPES}
                                    className="hidden"
                                    onChange={handleFileInput}
                                />
                                <button
                                    onClick={handleClear}
                                    className="text-sm text-slate-500 hover:text-slate-700 underline font-medium"
                                >
                                    Xóa toàn bộ
                                </button>
                            </div>

                            {/* Table */}
                            <div className="border border-slate-200 rounded-xl overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">
                                                {mode === 'translate' ? 'Tài liệu Gốc (Tiếng Việt)' : 'Tài liệu Tiếng Việt'}
                                            </th>
                                            {mode !== 'translate' && (
                                                <>
                                                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase w-16">Link</th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Tài liệu chuẩn (Tiếng Anh)</th>
                                                </>
                                            )}
                                            <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase w-20">Định dạng</th>
                                            <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase w-32">Credit</th>
                                            <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase w-24">Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {pairs.map(pair => (
                                            <tr key={pair.id} className={`${pair.status === 'benchmark_only' ? 'bg-red-50/50' : ''}`}>
                                                <td className="px-4 py-3">
                                                    {pair.source ? (
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <FileText className="w-4 h-4 text-indigo-500 shrink-0" />
                                                                <span className="text-sm font-medium text-slate-700 truncate">{pair.source.file.name}</span>
                                                            </div>
                                                            {parseWarnings.has(pair.id) && (
                                                                parseStatus.get(pair.id) === 'error' ? (
                                                                    <div className="mt-1.5 px-2.5 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-xs text-rose-700 font-medium flex items-start gap-1.5">
                                                                        <span className="text-base leading-none">😔</span>
                                                                        <span className="leading-snug">{parseWarnings.get(pair.id)![0]}</span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="mt-1 text-[11px] text-amber-600 flex items-center gap-1">
                                                                        <AlertTriangle className="w-3 h-3 shrink-0" />
                                                                        <span className="truncate">{parseWarnings.get(pair.id)![0]}</span>
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-slate-400 italic">No source</span>
                                                    )}
                                                </td>
                                                {mode !== 'translate' && (
                                                    <>
                                                        <td className="px-4 py-3 text-center">
                                                            {pair.status === 'paired' ? (
                                                                <Link2 className="w-4 h-4 text-emerald-500 mx-auto" />
                                                            ) : (
                                                                <Unlink className="w-4 h-4 text-slate-300 mx-auto" />
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {pair.benchmark ? (
                                                                <div className="flex items-center gap-2">
                                                                    <FileText className="w-4 h-4 text-emerald-500 shrink-0" />
                                                                    <span className="text-sm font-medium text-slate-700 truncate">{pair.benchmark.file.name}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-sm text-slate-400 italic">No benchmark</span>
                                                            )}
                                                        </td>
                                                    </>
                                                )}
                                                {/* Format Column */}
                                                <td className="px-4 py-3 text-center">
                                                    {pair.source && (
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            letterSpacing: '0.05em',
                                                            backgroundColor: getFormatLabel(pair.source.file.name) === 'DOCX' ? '#ede9fe'
                                                                : getFormatLabel(pair.source.file.name) === 'PDF' ? '#fce7f3'
                                                                    : '#f1f5f9',
                                                            color: getFormatLabel(pair.source.file.name) === 'DOCX' ? '#6d28d9'
                                                                : getFormatLabel(pair.source.file.name) === 'PDF' ? '#be185d'
                                                                    : '#475569',
                                                        }}>
                                                            {getFormatLabel(pair.source.file.name)}
                                                        </span>
                                                    )}
                                                </td>
                                                {/* Token Badge Column */}
                                                <td className="px-4 py-3 text-center">
                                                    {parsedTexts.has(pair.id) ? (
                                                        <TokenBadge
                                                            compact
                                                            credits={selectPrimaryCredits(bundleCredits.get(pair.id), creditMode)}
                                                            accurateStatus={accurateStatuses.get(pair.id) || 'idle'}
                                                            onRetryAccurate={() => retryAccurateCount(pair.id)}
                                                        />
                                                    ) : isEstimating ? (
                                                        <span className="text-xs text-slate-400">...</span>
                                                    ) : null}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        {pair.status === 'paired' && mode !== 'translate' && (
                                                            <button
                                                                onClick={() => handleUnlink(pair.id)}
                                                                className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                                                title="Unlink"
                                                            >
                                                                <Unlink className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleRemovePair(pair.id)}
                                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Remove"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between shrink-0 bg-slate-50">
                    <div className="text-sm font-semibold text-slate-500">
                        {droppedFiles.length > 0 && `Đã đưa vào ${droppedFiles.length} tài liệu`}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-slate-600 font-bold hover:bg-slate-200 rounded-lg transition-colors border border-slate-200"
                        >
                            Hủy bỏ
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={importableCount === 0 || isEstimating}
                            className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-100"
                            title={isEstimating ? 'Đang tính credits — đợi xong rồi nạp' : undefined}
                        >
                            {isEstimating ? 'Đang tính credits...' : `Tiến hành Nạp (${importableCount} file)`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchUploadModal;
